import { zodResolver } from '@hookform/resolvers/zod';
import { UserCircle2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ERROR_CODE } from '@obraplan/core/application/errors/error-codes';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Input } from '@/components/ui/input';
import { profileFormSchema, type ProfileFormValues } from '@/features/session/profile-form';
import { useContainer, useSession, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/**
 * Meu perfil — o cadastro que a própria pessoa corrige (o nome aparece em toda linha do
 * histórico). Papéis e situação são só leitura: mudá-los é de quem administra, para
 * ninguém ampliar o próprio acesso aqui.
 */
export function MyProfilePage() {
  const container = useContainer();
  const session = useSession();
  const { reload } = useSessionState();

  // As obras que o próprio perfil alcança: é o que nomeia o alcance de cada papel.
  const projects = useLive(
    () => container.listAccessibleProjects.execute(session),
    [session.userId],
  );

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: { name: session.userName, email: session.userEmail ?? '' },
  });

  const { reset } = form;

  // A sessão é recarregada depois de salvar; o formulário acompanha, senão continuaria
  // mostrando o que foi digitado como se ainda não tivesse sido gravado.
  useEffect(() => {
    reset({ name: session.userName, email: session.userEmail ?? '' });
  }, [reset, session.userName, session.userEmail]);

  const [save, saveState] = useCommand(
    (values: ProfileFormValues) =>
      container.updateMyProfile.execute(
        { name: values.name, ...(values.email === '' ? {} : { email: values.email }) },
        session,
      ),
    {
      successMessage: 'Perfil atualizado.',
      silent: true,
      // O nome no cabeçalho vem da sessão, e não do banco: sem recarregá-la, a pessoa
      // salvaria a correção e continuaria vendo o nome antigo no canto da tela.
      onSuccess: () => reload(),
    },
  );

  const fieldError = (field: string): string | undefined =>
    saveState.error?.fieldErrors.find((f) => f.field === field)?.message;

  const nameError =
    form.formState.errors.name?.message ??
    fieldError('name') ??
    (saveState.error?.code === ERROR_CODE.USER_NAME_TAKEN ? saveState.error.message : undefined);

  const emailError =
    form.formState.errors.email?.message ??
    fieldError('email') ??
    (saveState.error?.code === ERROR_CODE.USER_EMAIL_TAKEN ? saveState.error.message : undefined);

  const generalError =
    saveState.error !== undefined && nameError === undefined && emailError === undefined
      ? saveState.error.message
      : undefined;

  const assignments = session.assignments;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Meu perfil</h1>
        <p className="text-(--color-content-muted)">
          Como você aparece no histórico de alterações e por onde você entra no sistema.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle2 className="size-5" aria-hidden="true" />
            Seus dados
          </CardTitle>
          <CardDescription>
            O nome é o que fica registrado em cada alteração que você faz.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex max-w-md flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void form.handleSubmit((values) => save(values))(event);
            }}
          >
            <Field label="Nome" htmlFor="my-profile-name" required error={nameError}>
              <Input
                id="my-profile-name"
                aria-invalid={nameError !== undefined}
                {...form.register('name')}
              />
            </Field>

            <Field
              label="E-mail"
              htmlFor="my-profile-email"
              hint={
                session.userEmail === undefined
                  ? 'Seu perfil ainda não tem e-mail. É por ele que se entra no sistema.'
                  : 'É por este endereço que você entra. Em branco mantém o atual.'
              }
              error={emailError}
            >
              <Input
                id="my-profile-email"
                type="email"
                aria-invalid={emailError !== undefined}
                {...form.register('email')}
              />
            </Field>

            {generalError !== undefined && (
              <p role="alert" className="text-sm text-(--color-state-danger)">
                {generalError}
              </p>
            )}

            <div>
              <Button type="submit" variant="primary" disabled={saveState.isPending}>
                {saveState.isPending ? 'Salvando…' : 'Salvar'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Seus papéis</CardTitle>
          <CardDescription>O que você pode fazer, e em quais obras (só leitura).</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-1.5">
          {assignments.length === 0 ? (
            <p className="text-sm text-(--color-content-muted)">
              Nenhum papel atribuído. Fale com quem administra o sistema.
            </p>
          ) : (
            assignments.map((assignment) => {
              const role = session.roles.get(assignment.roleId);
              if (role === undefined) return null;
              const scope =
                assignment.projectId === undefined
                  ? 'todas as obras'
                  : (projects?.find((project) => project.id === assignment.projectId)?.name ??
                    'obra sem acesso');
              return (
                <Badge
                  key={`${assignment.roleId}-${assignment.projectId ?? 'global'}`}
                  tone={assignment.projectId === undefined ? 'warn' : 'info'}
                >
                  {role.key} · {scope}
                </Badge>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
